import { useEffect, useState } from "react";
import axios from "axios";
import { ChevronDown, Loader2, Search } from "lucide-react";
import { Overline, Reveal } from "@/components/Reveal";
import { API } from "@/data/campaign";

const POSITION_STYLES = {
  "Strongly agree": "bg-pine-700 text-paper",
  "Somewhat agree": "bg-pine-100 text-pine-700",
  "About the same as now": "bg-parch text-ink/60",
  "Somewhat disagree": "bg-terra-100 text-terra-600",
  "Strongly disagree": "bg-terra-500 text-paper",
};

const Questions = () => {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    axios.get(`${API}/questions`).then(({ data }) => setItems(data.items)).catch(() => setItems([]));
  }, []);

  const filtered = (items || []).filter((q) =>
    `${q.question} ${q.answer} ${q.category || ""}`.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <main data-testid="questions-page" className="pt-28 lg:pt-32">
      <section className="mx-auto max-w-4xl px-5 pb-24 sm:px-8">
        <Reveal>
          <Overline>Questions</Overline>
          <h1 className="mt-4 font-serif text-4xl font-medium leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
            Questions residents <span className="italic text-terra-500">are asking</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink/75 sm:text-lg">
            Here are answers to questions we hear from neighbours across Ward 11. Check back — we add new ones as they
            come in.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-10">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
            <input
              data-testid="questions-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search questions…"
              className="w-full border border-pine-700/25 bg-paper py-3.5 pl-11 pr-4 text-base text-ink placeholder:text-ink/40 focus:border-terra-500 focus:outline-none"
            />
          </div>
        </Reveal>

        <div className="mt-8 border-t border-pine-700/15" data-testid="questions-list">
          {!items ? (
            <div className="flex items-center gap-2 py-16 text-ink/50">
              <Loader2 className="h-5 w-5 animate-spin" /> Loading questions…
            </div>
          ) : filtered.length === 0 ? (
            <p className="py-16 text-center text-sm text-ink/50" data-testid="questions-empty">
              No questions match your search.
            </p>
          ) : (
            filtered.map((q, i) => {
              const isOpen = open === q.id;
              return (
                <div key={q.id} className="border-b border-pine-700/15">
                  <button
                    data-testid={`question-${i}`}
                    onClick={() => setOpen(isOpen ? null : q.id)}
                    className="flex w-full items-start justify-between gap-4 py-6 text-left"
                    aria-expanded={isOpen}
                  >
                    <span className="min-w-0">
                      <span className="block font-serif text-lg font-semibold leading-snug tracking-tight text-ink sm:text-xl">
                        {q.question}
                      </span>
                      <span className="mt-2 flex flex-wrap gap-2">
                        {q.position && (
                          <span className={`px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] ${POSITION_STYLES[q.position] || "bg-parch text-ink/60"}`}>
                            {q.position}
                          </span>
                        )}
                        {q.category && (
                          <span className="border border-pine-700/20 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-ink/55">
                            {q.category}
                          </span>
                        )}
                      </span>
                    </span>
                    <ChevronDown
                      className={`mt-1 h-5 w-5 shrink-0 text-pine-700 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  {isOpen && (
                    <div data-testid={`answer-${i}`} className="whitespace-pre-line pb-8 pr-8 text-base leading-relaxed text-ink/75">
                      {q.answer}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </section>
    </main>
  );
};

export default Questions;
